import { Box, Grid, List, ListItemButton, Stack } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllTeamMemberRecordsOfTeam,
  getAllWorkspaceTeams,
  getAllWorkspaceTeamsFilters,
  getMyTeams,
  ITeam,
  ITeamMembersResponse,
  selectedOrganizationalUnitTeams,
  deleteOUTeamMember,
  ITeamFilters,
} from 'wisestep-api';
import {
  Notification,
  WsDrawer,
  WsIconMoreVer,
  WsListingLoader,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import TeamAvatartGroup from '../../../components/AppLayoutNew/TeamAvatarGroup';
import NoShadowBox from '../../../components/NoShadowBox';
import { useWorkspaceContext } from '../../../context';
import { TeamAccessAction, useTeamAccessAuthz } from '../../../hooks/useTeamAccessAuthz';
import GroupAvatar from '../../../images/Avatar/Group.svg';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import PopoverOnClick from '../../clientListingPage/components/PopoverOnClick';
import { StyledPaginationBox } from '../../clientsSettings/components/OrganizationalUnitLayoutNew';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { teamFilters } from '../helper/defaultValues';

import CreateTeamButton from './CreateTeamButton';
import CreateTeamModal from './CreateTeamModal';
import TeamHistory from './TeamHistory';
import TeamListActions from './TeamListActions';
import { StyledDataGrid } from './TeamListNew';
import TeamMembersTable from './TeamMembersTable';
import TeamsEmptyList from './TeamsEmptyList';
import TeamSettings from './TeamSettings';
import WsAvatar from './WsAvatar';

interface ITeamTitleColumnProps {
  param: GridRenderCellParams;
  onClick: MouseEventHandler<Element>;
}

const TeamTitleColumn = ({ param, onClick }: ITeamTitleColumnProps) => (
  <Stack direction="row" spacing={2} alignItems="center">
    <WsAvatar src={GroupAvatar} variant="rounded" />
    <WsTypography
      cursor="pointer"
      variant={WsTypoVariants.SUB_TITLE}
      color={WsTypoColors.PRIMARY750}
      onClick={onClick}
    >
      {param.value}
    </WsTypography>
  </Stack>
);

interface ITeamListProps {
  forAnalyticsAndTeamMembersView?: boolean;
  readOnlyMode?: boolean;
  isOrganizationalUnit?: boolean;
  organizationalUnitId?: string;
  isTeamsOrganizationalUnit?: boolean;
}

const TeamList = ({
  forAnalyticsAndTeamMembersView = false,
  readOnlyMode = false,
  isOrganizationalUnit,
  organizationalUnitId,
  isTeamsOrganizationalUnit,
}: ITeamListProps) => {
  const admin = useWorkspaceContext();
  const teamsList = useRef<ITeam[]>([]);
  const teamsListCount = useRef(0);
  const [filteredTeamList, setFilteredTeamList] = useState<ITeam[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<ITeamMembersResponse[]>();
  const [selectedTeam, setSelectedTeam] = useState<ITeam>({} as ITeam);
  const [openTeamMemberDrawer, setOpenTeamMemberDrawer] = useState<boolean>(false);
  const [openTeamMembers, setOpenTeamMembers] = useState<boolean>(false);
  const [openEditTeamName, setOpenEditTeamName] = useState<boolean>(false);
  const [openTeamSettings, setOpenTeamSettings] = useState<boolean>(false);
  const [openTeamHistory, setOpenTeamHistory] = useState<boolean>(false);
  const [createTeamSuccess, setCreateTeamSuccess] = useState<boolean>(false);
  const [editTeamNameSuccess, setEditTeamNameSuccess] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [newTeamName, setNewTeamName] = useState<string>();
  const [pageSize, setPageSize] = useState<number>(50);
  const [pageNo, setPageNo] = useState<number>(0);
  const [query] = useState<ITeamFilters>(teamFilters);
  const navigate = useNavigate();

  const { access: teamAccessControl } = useTeamAccessAuthz([
    TeamAccessAction.MANAGE_TEAMS_ACCESS,
    TeamAccessAction.VIEW_TEAMS_ACCESS,
  ]);

  const viewTeamAccess = admin.owns || teamAccessControl[1];

  const teamCreationAllowed = admin.owns || teamAccessControl[0];

  const navigateToMembersPage = useCallback(
    (rowObj: ITeam, isTeamDrawerOpen?: boolean) => {
      if (forAnalyticsAndTeamMembersView) {
        navigate('/team/team-members-view', {
          state: {
            teamId: rowObj.id,
            teamName: rowObj.name,
          },
        });
      } else if (readOnlyMode) {
        setOpenTeamMemberDrawer(true);
      } else {
        navigate('./team-members/' + rowObj.id, {
          state: {
            teamId: rowObj.id,
            teamName: rowObj.name,
            teamOwnerEmail: rowObj?.owner,
            ...(typeof isTeamDrawerOpen !== undefined && { isTeamDrawerOpen: isTeamDrawerOpen }),
          },
        });
      }
    },
    [forAnalyticsAndTeamMembersView, readOnlyMode, navigate]
  );

  const openEdit = useCallback((rowObj: ITeam) => {
    setSelectedTeam(rowObj);
    setOpenEditTeamName(true);
  }, []);

  const openSettings = useCallback((rowObj: ITeam) => {
    setSelectedTeam(rowObj);
    setOpenTeamSettings(true);
  }, []);

  const openHistory = useCallback((rowObj: ITeam) => {
    setSelectedTeam(rowObj);
    setOpenTeamHistory(true);
  }, []);

  const getTeamsInWorkspace = useCallback(async () => {
    try {
      setLoader(true);
      if (isOrganizationalUnit) {
        const newresp = await selectedOrganizationalUnitTeams(organizationalUnitId ?? '');
        const teamIds: string[] = newresp.data.map((item) => item.teamId);
        if (teamIds && teamIds.length > 0) {
          const response = await getAllWorkspaceTeams();
          const similarItems: Array<ITeam> = [];
          response.data.forEach((item1) => {
            const matchingItem = newresp.data.find((item2) => item1.id === item2.teamId);
            if (matchingItem) {
              const mergedItem = {
                ...item1,
                organizationalId: matchingItem.organizationalId,
                value: matchingItem.value,
              };
              similarItems.push(mergedItem);
            }
          });
          setFilteredTeamList(similarItems);
          teamsList.current = similarItems;
        } else {
          setFilteredTeamList([]);
          teamsList.current = [];
        }
      } else {
        if (viewTeamAccess) {
          const response = await getAllWorkspaceTeamsFilters({
            ...query,
            pageNumber: pageNo + 1,
            pageSize: pageSize,
            workspaceId: admin?.id ?? '',
          });
          const value = response.headers['x-countofteams'];
          teamsListCount.current = parseInt(value);
          setFilteredTeamList(response.data);
          teamsList.current = response.data;
        } else {
          const response = await getMyTeams();
          setFilteredTeamList(response.data);
          teamsList.current = response.data;
          teamsListCount.current = response.data?.length ?? 0;
        }
      }
    } catch (error) {
      console.error('Error fetching teams', error);
    } finally {
      setLoader(false);
    }
  }, [
    isOrganizationalUnit,
    organizationalUnitId,
    viewTeamAccess,
    query,
    pageNo,
    pageSize,
    admin?.id,
  ]);

  const columns: GridColDef[] = [
    {
      headerName: 'Team Name',
      field: 'name',
      flex: 1,
      renderCell: (param) => (
        <TeamTitleColumn
          param={param}
          onClick={() => {
            navigateToMembersPage(param.row, admin.owns);
          }}
        />
      ),
    },
    {
      headerName: 'Users',
      field: 'memberCount',
      flex: 1,
      renderCell: (param) => (
        <WsTypography
          onClick={() => {
            navigateToMembersPage(param.row, false);
          }}
          cursor="pointer"
        >
          {param.value}
        </WsTypography>
      ),
    },
  ];

  if (!readOnlyMode) {
    columns.push({
      headerName: 'Actions',
      field: 'actions',
      renderCell: (param) => (
        <TeamListActions
          navigateToMembersPage={() => navigateToMembersPage(param.row)}
          openTeamNameEdit={() => openEdit(param.row)}
          openTeamSettings={() => openSettings(param.row)}
          openHistory={() => openHistory(param.row)}
        />
      ),
    });
  }

  if (admin.owns && isOrganizationalUnit) {
    columns.push({
      headerName: '',
      field: 'actions',
      width: 50,
      renderCell: (param) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <List sx={{ borderRadius: 5, width: 150 }}>
            <ListItemButton onClick={() => handleDeleteTeamMember(param.row)}>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                cursor="pointer"
                color={WsTypoColors.RED}
              >
                Remove Item
              </WsTypography>
            </ListItemButton>
          </List>
        </PopoverOnClick>
      ),
    });
  }

  const handleDeleteTeamMember = async (rowObj: any) => {
    const newresp = await selectedOrganizationalUnitTeams(rowObj.organizationalUnitId ?? '');
    newresp.data.forEach(async (item) => {
      if (rowObj.organizationalUnitId === item.organizationalId) {
        if (rowObj.teamId === item.teamId) {
          const payload = item.value;
          const res = await deleteOUTeamMember(payload);
          if (res.status === 200) {
            getTeamsInWorkspace();
            setNotification(success('Team removed from organization successfully!'));
          } else {
            setNotification(warning('Something went wrong!!'));
          }
        }
      }
    });
  };

  const data = useMemo(
    () =>
      filteredTeamList?.map((team) => ({
        id: team.id,
        teamId: team.id,
        name: team.name,
        memberCount: team.memberCount ? team.memberCount + 1 : 1,
        owner: team.ownerEmail,
        organizationalUnitId: organizationalUnitId,
      })) ?? [],
    [filteredTeamList, organizationalUnitId]
  );

  const getTeamsOfCurrentUser = useCallback(async () => {
    const response = await getMyTeams();
    teamsListCount.current = response.data?.length ?? 0;
    setFilteredTeamList(response.data);
  }, []);

  const getMembersOfTeam = async (team: ITeam) => {
    const teamId = team.id;
    const teamMembers: ITeamMembersResponse = {
      memberEmail: team.ownerEmail ?? '',
      name: '',
      role: 'owner',
      roles: null,
      teamId: team.id ?? '',
    };
    if (teamId) {
      const response = await getAllTeamMemberRecordsOfTeam(teamId, query);
      setTeamMembers([teamMembers, ...response.data.teamMembers]);
    } else {
      setTeamMembers([teamMembers]);
    }
  };

  const editTeamNameSuccessHandler = () => {
    setEditTeamNameSuccess(true);
    getTeamsInWorkspace();
  };

  const createTeamSuccessHandler = async (teamName?: string) => {
    setCreateTeamSuccess(true);
    await getTeamsInWorkspace();
    setNewTeamName(teamName);
  };

  const deleteMemberHandler = () => getMembersOfTeam(selectedTeam);

  useEffect(() => {
    isOrganizationalUnit || viewTeamAccess ? getTeamsInWorkspace() : getTeamsOfCurrentUser();
  }, [viewTeamAccess, getTeamsInWorkspace, getTeamsOfCurrentUser, isOrganizationalUnit]);

  useEffect(() => {
    if (newTeamName) {
      const newTeam = filteredTeamList.find((teamObj) => teamObj.name === newTeamName);
      if (newTeam)
        navigate('./team-members/' + newTeam.id, {
          state: {
            teamId: newTeam.id,
            teamName: newTeam.name,
            teamOwnerEmail: newTeam.ownerEmail,
          },
        });
    }
  }, [navigate, newTeamName, filteredTeamList]);

  const totalTeamsCount = isOrganizationalUnit ? data.length : teamsListCount.current;

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {totalTeamsCount}
        </WsTypography>
      </Box>
      {totalTeamsCount > 10 && (
        <WsDataGridPagination
          count={Math.ceil(totalTeamsCount / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <>
      <Stack direction={'row'} justifyContent={'flex-end'} my={2}>
        <Stack direction={'row'} alignItems={'center'}>
          {teamCreationAllowed && !readOnlyMode && (
            <CreateTeamButton
              onEditSuccess={editTeamNameSuccessHandler}
              onCreateSuccess={createTeamSuccessHandler}
              filteredTeamList={filteredTeamList}
              onFetch={getTeamsInWorkspace}
            />
          )}
          {organizationalUnitId && !isTeamsOrganizationalUnit && (
            <CreateTeamButton
              onEditSuccess={editTeamNameSuccessHandler}
              onCreateSuccess={createTeamSuccessHandler}
              organizationId={organizationalUnitId}
              filteredTeamList={filteredTeamList}
              onFetch={getTeamsInWorkspace}
              TeamsOrganizationalUnit={isTeamsOrganizationalUnit}
            />
          )}
        </Stack>
      </Stack>
      {loader && filteredTeamList?.length == 0 ? (
        <NoShadowBox>
          <WsListingLoader />
        </NoShadowBox>
      ) : (
        <Grid item xs={12}>
          {filteredTeamList?.length > 0 ? (
            <>
              {paginationComponent}
              <StyledDataGrid
                loading={loader}
                autoHeight
                hideFooter
                pageSize={pageSize}
                page={pageNo}
                columns={columns}
                rows={data}
              />
              {totalTeamsCount > 10 && paginationComponent}
            </>
          ) : (
            <TeamsEmptyList
              teamCreationAllowed={teamCreationAllowed ?? false}
              isOrganizationalUnit={!readOnlyMode ? true : false}
            />
          )}
        </Grid>
      )}
      {filteredTeamList?.length > 0 && (
        <>
          <WsDrawer
            title={selectedTeam.name ?? ''}
            open={openTeamMembers}
            onClose={() => setOpenTeamMembers(false)}
          >
            <TeamMembersTable
              teamMembers={teamMembers || []}
              onDeleteMember={deleteMemberHandler}
            />
          </WsDrawer>
          <TeamSettings
            open={openTeamSettings}
            handleClose={() => setOpenTeamSettings(false)}
            team={selectedTeam}
          />
          <TeamHistory
            team={selectedTeam}
            open={openTeamHistory}
            handleClose={() => setOpenTeamHistory(false)}
          />
          <CreateTeamModal
            open={openEditTeamName}
            onEditSuccess={createTeamSuccessHandler}
            onCreateSuccess={createTeamSuccessHandler}
            handleClose={() => setOpenEditTeamName(false)}
            teamId={selectedTeam.id ?? ''}
          />
        </>
      )}
      {createTeamSuccess && (
        <Notification
          vertical="top"
          horizontal="center"
          autoHideDuration={3000}
          notificationObject={{
            message: 'Team created successfully!',
            type: 'success',
          }}
          notify={true}
        />
      )}
      {editTeamNameSuccess && (
        <Notification
          vertical="top"
          horizontal="center"
          autoHideDuration={3000}
          notificationObject={{
            message: 'Team name changed successfully!',
            type: 'success',
          }}
          notify={true}
        />
      )}
      {openTeamMemberDrawer && <TeamAvatartGroup defaultTeam={selectedTeam} />}
    </>
  );
};

export default TeamList;
